/* eslint-disable max-len */
import { CircularProgress, makeStyles } from '@material-ui/core';
import { Entity, EntityAttributeType } from '@moderepo/mode-internal-apis';
import clsx from 'clsx';
import { CSSProperties, useCallback } from 'react';
import { EntityBasicViewDesignOther, EntityBasicViewDesignDefault } from '.';
import { EntityUIComponentCollection } from '..';
import { useRitzTranslation } from '../../../utils/customHooks';
import { useSelectEntity, useSelectProject } from '../../../utils/dataStateHooks';
import { SettingsErrorLogger } from '../../../utils/logger';
import { RitzComponentsCollection } from '../../../utils/ritzComponentsCollection';
import { isObjectLiteral, isValidSettingBooleanValue, isValidSettingStringValue } from '../../../utils/utils';
import { EntityUIComponentProps } from '../EntityDetailsLayouts';


const useStyle = makeStyles(() => {
    return {
        root: {
            position      : 'relative',
            overflow      : 'hidden',
            display       : 'flex',
            alignItems    : 'center',
            justifyContent: 'center',
            height        : '100%',
            '&.clickable' : {
                cursor: 'pointer',
            },
        },
    };
}, { name: 'EntityBasicViewUIComponent', index: 1 });



/**
 * The list of all the different designed supported by the EntityBasicView component
 */
export enum EntityBasicViewDesign {
    DEFAULT = 'Default',
    DESIGN_1 = 'Design_1',
    DESIGN_2 = 'Design_2'
}


/**
 * This is the interface for the EntityBasicViewUIComponent's CustomSettings.
 */
export interface EntityBasicViewUIComponentCustomSettings {
    // The view design to use. The default value is 'Default' design so this option is not required unless we want to use a different design
    readonly viewDesign?: EntityBasicViewDesign | undefined;

    // Whether or not to show the entity description in the view. The default is TRUE unless this is specifically set to false
    readonly showDescription?: boolean | undefined;

    // Whether or not to show the entity class name in the view
    readonly showEntityClass?: boolean | undefined;

    // Whether or not to show the entity's image, the default is true unless specifically set to false
    readonly showImage?: boolean | undefined;

    // The default image url to use if the entity does not have an image
    readonly defaultImageUrl?: string | undefined;

    // Whether or not to show "No Image" message when "showImage" is set to true but the entity does not have an image or there is no defaultImageUrl
    readonly showNoImageMessage?: boolean | undefined;

    // Whether or not to show the icon, the default is false AND this also depends on whether or not `showImage` is true.
    // If `showImage` is false, this option won't matter.
    readonly showIcon?: boolean | undefined;

    // The default icon image url to use if the entity does not have an icon
    readonly defaultIconUrl?: string | undefined;

    // Whether or not to also show attributes. Default is FALSE
    readonly showAttributes?: boolean | undefined;

    // To specify the list of attributes to show if we don't want to show all attributes.
    readonly includeAttributes?: readonly string[] | undefined;

    // To specify the list of attributes to EXCLUDE if we don't want to show all attributes
    readonly excludeAttributes?: readonly string[] | undefined;

    // To specify the list of attribute types to exclude. Sometimes attributes of type OBJECT and ARRAY can be large and we want to exclude them.
    readonly excludeAttributeTypes?: readonly EntityAttributeType[] | undefined;

    // The custom CSS style for the outer container element
    readonly containerStyle?: CSSProperties | undefined;

    // The custom CSS style for the image element
    readonly imageStyle?: CSSProperties | undefined;

    // The custom CSS style for the icon element
    readonly iconStyle?: CSSProperties | undefined;

    // The custom CSS style for the entity name/description content element
    readonly infoStyle?: CSSProperties | undefined;

    // The custom CSS style for the entity name element
    readonly nameStyle?: CSSProperties | undefined;

    // The custom CSS style for the entity description element
    readonly descriptionStyle?: CSSProperties | undefined;
}


/**
 * Type guard function to validate the obj to make sure it is a type of EntityImageViewUIComponentCustomSettings
 */
export const isEntityBasicViewUIComponentCustomSettings = (
    obj: unknown,
    errorLogger?: SettingsErrorLogger | undefined,
    currentConfigPath?: readonly string[] | undefined,
): obj is EntityBasicViewUIComponentCustomSettings => {
    const settings = obj as EntityBasicViewUIComponentCustomSettings;
    const configPath = [...(currentConfigPath ?? [])];
    const viewDesignNames = Object.values(EntityBasicViewDesign);

    if (!isObjectLiteral(settings)) {
        return errorLogger?.logError({
            message: 'Value must be undefined or an object.',
            value  : settings,
            path   : [...configPath],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.viewDesign, false, false, viewDesignNames)) {
        return errorLogger?.logError({
            message: `"viewDesign" must be undefined or one of these values [${
                viewDesignNames.map((design) => {
                    return `"${design}"`;
                }).join(', ')}].`,
            value: settings.viewDesign,
            path : [...configPath, 'viewDesign'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showDescription)) {
        return errorLogger?.logError({
            message: '"showDescription" must be undefined or a boolean.',
            value  : settings.showDescription,
            path   : [...configPath, 'showDescription'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showEntityClass)) {
        return errorLogger?.logError({
            message: '"showEntityClass" must be undefined or a boolean.',
            value  : settings.showEntityClass,
            path   : [...configPath, 'showEntityClass'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showImage)) {
        return errorLogger?.logError({
            message: '"showImage" must be undefined or a boolean.',
            value  : settings.showImage,
            path   : [...configPath, 'showImage'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showIcon)) {
        return errorLogger?.logError({
            message: '"showIcon" must be undefined or a boolean.',
            value  : settings.showIcon,
            path   : [...configPath, 'showIcon'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showNoImageMessage)) {
        return errorLogger?.logError({
            message: '"showNoImageMessage" must be undefined or a boolean.',
            value  : settings.showNoImageMessage,
            path   : [...configPath, 'showNoImageMessage'],
        }) ?? false;
    }

    if (!isValidSettingBooleanValue(settings.showAttributes)) {
        return errorLogger?.logError({
            message: '"showAttributes" must be undefined or a boolean.',
            value  : settings.showAttributes,
            path   : [...configPath, 'showAttributes'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultImageUrl)) {
        return errorLogger?.logError({
            message: '"defaultImageUrl" must be undefined or a non-empty string.',
            value  : settings.defaultImageUrl,
            path   : [...configPath, 'defaultImageUrl'],
        }) ?? false;
    }

    if (!isValidSettingStringValue(settings.defaultIconUrl)) {
        return errorLogger?.logError({
            message: '"defaultIconUrl" must be undefined or a non-empty string.',
            value  : settings.defaultImageUrl,
            path   : [...configPath, 'defaultIconUrl'],
        }) ?? false;
    }

    // "includeAttributes" is optional but if it is provided, it must be an Array of strings
    if (settings.includeAttributes !== undefined) {
        if (!(settings.includeAttributes instanceof Array)) {
            return errorLogger?.logError({
                message: '"includeAttributes" must be undefined or an array of non-empty strings.',
                value  : settings.includeAttributes,
                path   : [...configPath, 'includeAttributes'],
            }) ?? false;
        }

        // Make sure every metricName in "includeAttributes" are non-empty strings
        if (!settings.includeAttributes.every((attributeKey, index) => {
            if (!isValidSettingStringValue(attributeKey, true)) {
                return errorLogger?.logError({
                    message: 'Every values in "includeAttributes" must be non-empty strings.',
                    value  : attributeKey,
                    path   : [...configPath, 'includeAttributes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // "excludeAttributes" is optional but if it is provided, it must be an Array of strings
    if (settings.excludeAttributes !== undefined) {
        if (!(settings.excludeAttributes instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeAttributes" must be undefined or an array of non-empty strings.',
                value  : settings.excludeAttributes,
                path   : [...configPath, 'excludeAttributes'],
            }) ?? false;
        }

        // Make sure every metricName in "excludeAttributes" are NOT non-empty strings
        if (!settings.excludeAttributes.every((attributeKey, index) => {
            if (!isValidSettingStringValue(attributeKey, true)) {
                return errorLogger?.logError({
                    message: 'Every items in "excludeAttributes" must be non-empty strings.',
                    value  : attributeKey,
                    path   : [...configPath, 'excludeAttributes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }

    // "excludeAttributeTypes" is optional but if it is provided, it must be an Array of attribute types
    if (settings.excludeAttributeTypes !== undefined) {
        if (!(settings.excludeAttributeTypes instanceof Array)) {
            return errorLogger?.logError({
                message: '"excludeAttributeTypes" must be undefined or an array of non-empty strings.',
                value  : settings.excludeAttributeTypes,
                path   : [...configPath, 'excludeAttributeTypes'],
            }) ?? false;
        }

        const attrTypes = Object.values(EntityAttributeType);

        // Make sure every values in "excludeAttributeTypes" are one of the EntityAttributeType
        if (!settings.excludeAttributeTypes.every((attributeType, index) => {
            if (!attrTypes.includes(attributeType)) {
                return errorLogger?.logError({
                    message: `Every values in "excludeAttributeTypes" must be one of the following [${
                        attrTypes.map((type) => {
                            return `"${type}"`;
                        }).join(', ')
                    }]`,
                    value: attributeType,
                    path : [...configPath, 'excludeAttributeTypes', `[${index}]`],
                }) ?? false;
            }
            return true;
        })) {
            return false;
        }
    }


    // "containerStyle", "imageStyle", "iconStyle", "infoStyle", "nameStyle", "descriptionStyle" are optional but if they are provided, they must be objects
    if (settings.containerStyle !== undefined && !isObjectLiteral(settings.containerStyle)) {
        return errorLogger?.logError({
            message: '"containerStyle" must be undefined or a CSSProperties object.',
            value  : settings.containerStyle,
            path   : [...configPath, 'containerStyle'],
        }) ?? false;
    }

    if (settings.imageStyle !== undefined && !isObjectLiteral(settings.imageStyle)) {
        return errorLogger?.logError({
            message: '"imageStyle" must be undefined or a CSSProperties object.',
            value  : settings.imageStyle,
            path   : [...configPath, 'imageStyle'],
        }) ?? false;
    }

    if (settings.iconStyle !== undefined && !isObjectLiteral(settings.iconStyle)) {
        return errorLogger?.logError({
            message: '"iconStyle" must be undefined or a CSSProperties object.',
            value  : settings.iconStyle,
            path   : [...configPath, 'iconStyle'],
        }) ?? false;
    }

    if (settings.infoStyle !== undefined && !isObjectLiteral(settings.infoStyle)) {
        return errorLogger?.logError({
            message: '"infoStyle" must be undefined or a CSSProperties object.',
            value  : settings.infoStyle,
            path   : [...configPath, 'infoStyle'],
        }) ?? false;
    }

    if (settings.nameStyle !== undefined && !isObjectLiteral(settings.nameStyle)) {
        return errorLogger?.logError({
            message: '"nameStyle" must be undefined or a CSSProperties object.',
            value  : settings.nameStyle,
            path   : [...configPath, 'nameStyle'],
        }) ?? false;
    }

    if (settings.descriptionStyle !== undefined && !isObjectLiteral(settings.descriptionStyle)) {
        return errorLogger?.logError({
            message: '"descriptionStyle" must be undefined or a CSSProperties object.',
            value  : settings.descriptionStyle,
            path   : [...configPath, 'descriptionStyle'],
        }) ?? false;
    }

    return true;
};


export interface EntityBasicViewUIComponentProps extends EntityUIComponentProps {
    readonly customSettings?: EntityBasicViewUIComponentCustomSettings | undefined;
}


/**
 * This EntityUIComponent is used for showing an entity basic info e.g. name, description, image, and icon
 */
export const EntityBasicViewUIComponent: React.FC<EntityBasicViewUIComponentProps> = (props: EntityBasicViewUIComponentProps) => {

    const selectedEntity = useSelectEntity(props.projectId, props.entityId, false);
    const classes = useStyle();
    const project = useSelectProject(props.projectId);
    const { trans } = useRitzTranslation(project);


    const createView = useCallback((entity: Entity) => {
        switch (props.customSettings?.viewDesign) {
            case 'Design_1':
            case 'Design_2':
                return (
                    <EntityBasicViewDesignOther
                        className={clsx(props.className, classes.root, props.onClick && 'clickable')}
                        style={props.style}
                        design={props.customSettings?.viewDesign}
                        projectId={props.projectId}
                        selectedEntity={entity}
                        texts={{
                            noImageMessage: trans('label.no_image'),
                        }}
                        {...props.customSettings}
                        onClick={props.onClick}
                    />
                );
            default:
                if (props.customSettings?.viewDesign !== undefined && props.customSettings?.viewDesign !== 'Default') {
                    // If viewDesign is provided but it is not one of the supported viewDesign, show some error so we know the config is wrong
                    // eslint-disable-next-line no-console
                    console.error(`Invalid viewDesign '${props.customSettings?.viewDesign}'`);
                }
                return (
                    <EntityBasicViewDesignDefault
                        className={clsx(props.className, classes.root, props.onClick && 'clickable')}
                        style={props.style}
                        projectId={props.projectId}
                        selectedEntity={entity}
                        texts={{
                            noImageMessage: trans('label.no_image'),
                        }}
                        {...props.customSettings}
                        onClick={props.onClick}
                    />
                );
        }
    }, [classes.root, props.className, props.customSettings, props.onClick, props.projectId, props.style, trans]);


    return (
        <>
            {selectedEntity && createView(selectedEntity)}

            {!selectedEntity && (
                <CircularProgress />
            )}
        </>
    );
};


// Register this component with the RitzComponentsCollection so that it can be used with the Ritz UI customization system
RitzComponentsCollection.getInstance().registerComponent(
    'EntityBasicViewUIComponent', EntityBasicViewUIComponent,
);



// Register the component with the EntityUIComponentCollection so that the component can be used by the Entity Layout System
EntityUIComponentCollection.getInstance().registerComponent(
    'Entity_Basic_View_Comp',
    {
        componentFunc          : EntityBasicViewUIComponent,
        customSettingsValidator: isEntityBasicViewUIComponentCustomSettings,
    },
);
